import React, { useContext, useEffect, useState } from 'react';
import { Components } from '../../styled';
import ProjectTableHeader from '../../../../components/ProjectTableHeader';
import useFormTextState from '../../../../hooks/useFormTextState';
import ProjectTable from '../../../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../../../components/ProjectTable/constants';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import useModalState from '../../../../hooks/useModalState';
import { MentorBoard } from '../../../../api/types/mentorBoard';
import { deleteMentorBoard, getAllMentorBoards, reorderMentorBoards } from '../../../../api/mentorBoard';
import { ToastContext } from '../../../../providers/ToastProvider/context';
import { isProjectError } from '../../../../utils/errors';
import CreateOrEditMentorBoardModal from '../../../../components/modals/CreateOrEditMentorBoardModal';

const MentorBoards = ({
  setShouldRefreshContext,
}: {
  setShouldRefreshContext: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast } = useContext(ToastContext);

  const [data, setData] = useState<MentorBoard[]>([]);

  const searchProps = useFormTextState();

  const createBoardModalProps = useModalState<{ board?: MentorBoard }>();

  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllMentorBoards();
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
  };

  useEffect(() => {
    refreshData().then();
  }, []);

  const onAddBoard = () => {
    createBoardModalProps.open({ board: undefined });
  };
  const onEditBoard = (id: string) => {
    const foundBoard = data?.find((item) => item?.id === id);
    if (foundBoard) {
      createBoardModalProps.open({ board: foundBoard });
    }
  };

  const onDeleteBoard = async (id: string) => {
    setIsLoading(true);
    await deleteMentorBoard(id);
    setIsLoading(false);
    await refreshData();
  };

  const onMoveBoard = async (id: string, offset: -1 | 1) => {
    const index = data.findIndex((item) => item?.id === id);
    const targetIndex = index + offset;
    if (index < 0 || targetIndex < 0 || targetIndex >= data.length) {
      return;
    }
    const reordered = [...data];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    setIsLoading(true);
    const result = await reorderMentorBoards(reordered.map((item) => item.id));
    setIsLoading(false);
    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    }
    await refreshData();
    setShouldRefreshContext(true);
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData?.().then();
      setShouldRefreshContext(true);
    }
    createBoardModalProps.onModalClose();
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);
  return (
    <Components.ComponentContainer>
      <ProjectTableHeader
        onSearchChange={searchProps.onChangeText}
        searchLabel={'Search by board title or ID'}
        searchPlaceholder={'Enter search term'}>
        <Components.SearchBarSeparator />
        <Components.PrimaryButton onClick={onAddBoard}>Add Mentor Board</Components.PrimaryButton>
      </ProjectTableHeader>
      <Components.TableWrapper>
        <ProjectTable
          data={data || []}
          columns={tableColumns.mentorBoards({
            onEdit: onEditBoard,
            onDelete: onDeleteBoard,
            onMoveUp: (id) => onMoveBoard(id, -1),
            onMoveDown: (id) => onMoveBoard(id, 1),
          })}
          filteringModel={_filteringModel}
          showRowCountText={'Found mentor boards'}
          customPageSize={15}
        />
      </Components.TableWrapper>
      <CreateOrEditMentorBoardModal
        {...createBoardModalProps}
        onModalClose={onCreateOrEditModalClose}
        refreshCallback={refreshData}
      />
    </Components.ComponentContainer>
  );
};

export default MentorBoards;
